import type { WebGLRenderer } from 'three'
import type { InputSource, PilotInput } from './types'

export type PoseMode = 'grip' | 'aim'

/**
 * Real Quest controllers. Poses are read in the `local-floor` reference space,
 * which *is* tracking space — the rig transform is what carries them into the
 * world, so nothing here needs to know the pilot's attitude (DESIGN.md §2).
 */
export class XRSource implements InputSource {
  readonly name = 'xr'

  /** Latest XRFrame, set by the render loop before `sample`. */
  frame: XRFrame | null = null
  referenceSpace: XRReferenceSpace | null = null

  /**
   * Rising-edge flags consumed by the app.
   *   right A — recalibrate standing pose      right B — respawn
   *   left  X — toggle the wrist panel          left  Y — cycle preset
   *   right squeeze — drag a panel slider
   */
  calibratePressed = false
  respawnPressed = false
  panelTogglePressed = false
  presetCyclePressed = false
  /** Right thumbstick click — flips grip/aim nozzle pose in flight. */
  poseTogglePressed = false
  /** True while the player is squeezing to interact with the wrist panel. */
  interactHeld = false
  /** Raw trigger per hand, kept separate from throttle so UI can use it. */
  readonly triggerHeld: [boolean, boolean] = [false, false]

  /**
   * Which controller space the nozzle is bolted to.
   *
   * DESIGN.md §2 says "aim pose", written before anything ran on a headset.
   * On Touch controllers the aim ray is tilted roughly 45-60° forward of the
   * handle, because it exists for pointing at menus. Bolting the nozzle to it
   * means that to thrust straight up you must crank your wrist until the
   * *laser* points at the floor — well past the natural pose.
   *
   * `grip` puts the nozzle along the handle axis: hold the controller like a
   * torch aimed at the ground and you go up. Default, and switchable so the
   * two can be compared in one session rather than argued about.
   */
  poseMode: PoseMode = 'grip'

  private readonly prev = { calibrate: false, respawn: false, panel: false, preset: false, pose: false }

  constructor(private readonly renderer: WebGLRenderer) {}

  sample(out: PilotInput, _dt: number): void {
    const frame = this.frame
    const space = this.referenceSpace
    const session = this.renderer.xr.getSession()
    if (!frame || !space || !session) {
      out.head.tracked = false
      out.hands[0].tracked = false
      out.hands[1].tracked = false
      return
    }

    const viewer = frame.getViewerPose(space)
    if (viewer) {
      const t = viewer.transform
      out.head.position.set(t.position.x, t.position.y, t.position.z)
      out.head.quaternion.set(
        t.orientation.x,
        t.orientation.y,
        t.orientation.z,
        t.orientation.w,
      )
      out.head.tracked = true
    } else {
      out.head.tracked = false
    }

    let calibrate = false
    let respawn = false
    let panelToggle = false
    let presetCycle = false
    let interact = false
    let poseToggle = false

    // Clear both hands before reading the session. A controller that goes to
    // sleep or runs out of battery simply vanishes from `inputSources` — it
    // does not report itself as untracked — so anything left over from the last
    // frame would persist. Without this, dropping a controller mid-flight
    // leaves its rocket firing at whatever the trigger last read, and it flies
    // you into the ground. Clearing lets the §2 tracking-loss decay handle it.
    for (const hand of out.hands) {
      hand.tracked = false
      hand.throttle = 0
    }
    this.triggerHeld[0] = false
    this.triggerHeld[1] = false

    for (const src of session.inputSources) {
      if (src.handedness !== 'left' && src.handedness !== 'right') continue
      const index = src.handedness === 'left' ? 0 : 1
      const hand = out.hands[index]

      const preferred =
        this.poseMode === 'grip' ? (src.gripSpace ?? src.targetRaySpace) : src.targetRaySpace
      const pose = preferred ? frame.getPose(preferred, space) : undefined
      if (pose) {
        const t = pose.transform
        hand.position.set(t.position.x, t.position.y, t.position.z)
        hand.quaternion.set(t.orientation.x, t.orientation.y, t.orientation.z, t.orientation.w)
        hand.tracked = !pose.emulatedPosition
      } else {
        hand.tracked = false
      }

      const gp = src.gamepad
      if (gp) {
        // xr-standard: 0 = trigger, 1 = squeeze, 4/5 = A/B (right) or X/Y (left).
        hand.throttle = gp.buttons[0]?.value ?? 0
        this.triggerHeld[index] = (gp.buttons[0]?.value ?? 0) > 0.55
        if (index === 1) {
          if (gp.buttons[1]?.pressed) interact = true
          if (gp.buttons[4]?.pressed) calibrate = true
          if (gp.buttons[5]?.pressed) respawn = true
          if (gp.buttons[3]?.pressed) poseToggle = true
        } else {
          if (gp.buttons[4]?.pressed) panelToggle = true
          if (gp.buttons[5]?.pressed) presetCycle = true
        }
      }
    }

    this.calibratePressed = calibrate && !this.prev.calibrate
    this.respawnPressed = respawn && !this.prev.respawn
    this.panelTogglePressed = panelToggle && !this.prev.panel
    this.presetCyclePressed = presetCycle && !this.prev.preset
    this.poseTogglePressed = poseToggle && !this.prev.pose
    this.prev.pose = poseToggle
    this.prev.calibrate = calibrate
    this.prev.respawn = respawn
    this.prev.panel = panelToggle
    this.prev.preset = presetCycle
    this.interactHeld = interact
  }
}
